import PageShell from '@/pages/shared/PageShell';

export default function CompareAITools() {
  return (
    <PageShell
      title="AIプロジェクト管理ツール比較｜AI搭載PMツールの選び方｜”おもいむすび”"
      description="AIを搭載したプロジェクト管理ツールの比較。AIタスク分解、AIスケジュール作成、AI進捗管理、AI日報解析など、各ツールのAI機能を詳しく比較します。"
      keywords="AI プロジェクト管理ツール 比較,AI PMツール,AI タスク管理 比較,プロジェクト管理 AI 比較"
      canonical="https://omoimusubi.asia/compare/ai-project-management-tools/"
      h1="AIプロジェクト管理ツールの比較と選び方"
      subtitle="近年増えているAI搭載のプロジェクト管理ツール。どのようなAI機能があり、何を基準に選ぶべきかを解説します。"
      relatedLinks={[
        { label: 'プロジェクト管理ツール比較', path: '/compare/project-management-tools/' },
        { label: 'プロジェクト管理に生成AIを活用', path: '/guides/project-management-ai/' },
        { label: 'AIタスク分解', path: '/features/ai-task-breakdown/' },
        { label: 'AIガントチャート', path: '/features/ai-gantt/' },
      ]}
    >
      <div className="not-prose mb-8 rounded-xl overflow-hidden border border-background-200/70">
        <img
          src="https://readdy.ai/api/search-image?query=A%20bright%20modern%20Japanese%20office%20scene%20a%20Japanese%20IT%20manager%20a%20man%20in%20his%2030s%20looking%20thoughtfully%20at%20a%20large%20display%20monitor%20showing%20an%20AI%20powered%20project%20management%20dashboard%20with%20automated%20task%20suggestions%20and%20predictive%20analytics%20the%20screen%20glows%20with%20clean%20colorful%20data%20visualizations%20natural%20daylight%20through%20large%20windows%20indoor%20plants%20on%20the%20desk%20contemporary%20refreshing%20tech%20workspace%20atmosphere&width=1200&height=600&seq=compare-ai-tools-01&orientation=landscape"
          alt="AIプロジェクト管理ツール比較 - AIが提案するタスクを確認するITマネージャー"
          className="w-full h-auto"
        />
      </div>
      <section>
        <h2>AIプロジェクト管理ツールとは</h2>
        <p>AIプロジェクト管理ツールとは、人工知能（AI）を活用してプロジェクト管理業務の一部を自動化・効率化するツールです。従来のプロジェクト管理ツールにAI機能が追加されることで、タスクの自動作成、スケジュールの最適化、進捗の自動分析などが可能になっています。</p>
      </section>

      <div className="not-prose my-8 overflow-x-auto">
        <h3 className="text-base font-bold text-foreground-900 mb-4">AIプロジェクト管理ツールの機能比較</h3>
        <table className="min-w-full border-collapse text-xs">
          <thead>
            <tr className="border-b border-background-300">
              <th className="text-left p-3 font-bold text-foreground-900 bg-background-100 sticky left-0 min-w-[200px]">AI機能</th>
              <th className="text-center p-3 font-bold text-primary-700 bg-primary-50 min-w-[140px]">”おもいむすび”</th>
              <th className="text-center p-3 font-bold text-foreground-900 bg-background-100 min-w-[140px]">一般的なAIツール</th>
            </tr>
          </thead>
          <tbody className="text-foreground-600">
            {[
              { label: '資料からのタスク自動抽出', tm: '◎（PDF/Excel/Word対応、担当者・期限・依存関係を提案）', other: '△（一部ツールのみ、要約レベルにとどまる）' },
              { label: 'タスク分解の粒度', tm: '◎（具体的な作業レベルまで分解、成果物も特定）', other: '△（大項目レベルの分解が多い）' },
              { label: 'WBS自動生成', tm: '◎（資料から階層構造で自動生成）', other: '×（ほぼ対応なし）' },
              { label: 'ガントチャート自動生成', tm: '◎（タスクから自動生成、依存関係・マイルストーン含む）', other: '△（部分的に自動化）' },
              { label: '日報のAI解析', tm: '◎（自由文から進捗・課題・次のタスクを抽出）', other: '△（要約のみのツールが多い）' },
              { label: '進捗の自動更新', tm: '◎（日報解析結果でタスクステータスを自動更新）', other: '×（手動更新が主流）' },
              { label: 'AIファイル質問応答', tm: '◎（資料内容に対し根拠付きで回答）', other: '△（一部ツールで実装開始）' },
              { label: '国別祝日の自動考慮', tm: '◎（メンバーの所属国に応じてスケジュールを自動調整）', other: '×（対応なし）' },
              { label: 'AIによるスケジュール再計算', tm: '◎（変更の影響範囲を自動計算）', other: '△（一部ツールで実装）' },
              { label: '人の承認フロー', tm: '◎（AI提案→人による確認・修正・承認）', other: '△（AI任せになるケースも）' },
            ].map((row) => (
              <tr key={row.label} className="border-b border-background-200/70 hover:bg-background-100/50">
                <td className="p-3 font-medium text-foreground-800 bg-background-100/50 sticky left-0">{row.label}</td>
                <td className="p-3 text-center font-medium text-primary-700">{row.tm}</td>
                <td className="p-3 text-center">{row.other}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="text-xs text-foreground-500 mt-3">※ 比較情報は2026年7月時点の一般的な傾向にもとづく参考情報です。各ツールの最新機能は各社公式サイトでご確認ください。</p>
      </div>

      <section>
        <h2>AIプロジェクト管理ツールを選ぶ際の5つのチェックポイント</h2>
        <ol>
          <li><strong>AIの提案を人が確認・修正できるか</strong>：AIがすべてを自動決定するのではなく、最終判断は人が行える設計が重要です。”おもいむすび”では、AIの提案内容をユーザーが確認・修正・承認してからプロジェクトに反映します。</li>
          <li><strong>AIが参照するデータの範囲</strong>：単なるチャットAIではなく、実際のプロジェクト資料や日報データをAIが読み込んで処理できるかがポイントです。</li>
          <li><strong>AI機能と既存のプロジェクト管理機能の統合度</strong>：AI機能がアドオン的に追加されているのではなく、タスク管理やガントチャートとシームレスに連携していることが重要です。</li>
          <li><strong>多言語対応</strong>：日本語でのAI処理に対応しているか、海外チームとの共同利用でAIが多言語を扱えるかを確認しましょう。</li>
          <li><strong>セキュリティとデータの取り扱い</strong>：AIにプロジェクト資料を読み込ませる以上、データの保存場所やAIの学習利用ポリシーを確認することが不可欠です。</li>
        </ol>
      </section>

      <section>
        <h2>AIに任せられること、任せられないこと</h2>
        <p>AIはプロジェクト管理の様々な場面で強力な支援を提供しますが、以下の点は人が責任を持って判断する必要があります。</p>
        <ul>
          <li><strong>AIに任せられること</strong>：タスクの抽出と整理、スケジュールの計算、日報の内容整理、資料からの情報検索、進捗データの集計</li>
          <li><strong>人が判断すべきこと</strong>：タスクの優先順位の最終判断、ステークホルダーとの交渉、リスク評価と対策の決定、チームメンバーのアサインと評価、プロジェクトの方向性の決定</li>
        </ul>
      </section>
    </PageShell>
  );
}